/**
 * appraisalClassifier.ts
 *
 * Pure classifier that maps an EPR total + 360 average into one of the
 * five canonical buckets from the Performance Review Framework's
 * "How to read the combination of EPR & 360 Review Result" table:
 *
 *   HIGH / HIGH    — Key Talent
 *   HIGH / LOW     — Behaviour Concern (coach, do not promote)
 *   MIXED / MIXED  — Solid Performer (standard increment)
 *   LOW  / HIGH    — Possible Manager Bias (investigate)
 *   LOW  / LOW     — At Risk (PIP / Exit)
 *
 * Thresholds match the framework exactly:
 *   High  ≥ 3.75
 *   Mixed 3.00 – 3.74
 *   Low   < 3.00
 *
 * This is the layer that makes the EPR + 360 split actionable for HR
 * — surfacing the Recommended Action right next to the appraisal
 * reduces manager bias because Low/High patterns get flagged for
 * investigation rather than waved through as low-performer judgments.
 */

export type AppraisalBucket =
  | 'high_high'
  | 'high_low'
  | 'mixed_mixed'
  | 'low_high'
  | 'low_low'
  | 'borderline'
  | 'incomplete';

export type AppraisalTone = 'success' | 'info' | 'warning' | 'error' | 'neutral';

export interface AppraisalClassification {
  bucket:    AppraisalBucket;
  label:     string;
  meaning:   string;
  action:    string;
  tone:      AppraisalTone;
  /** True when the framework explicitly recommends checking manager bias. */
  flagBias?: boolean;
}

// ─── Thresholds (do not change without HR sign-off — CLAUDE.md §I) ─

export const HIGH_THRESHOLD  = 3.75;
export const MIXED_THRESHOLD = 3.00;

export type Band = 'high' | 'mixed' | 'low';

export function bandOf(score: number | undefined | null): Band | null {
  if (typeof score !== 'number' || !Number.isFinite(score)) return null;
  if (score >= HIGH_THRESHOLD)  return 'high';
  if (score >= MIXED_THRESHOLD) return 'mixed';
  return 'low';
}

// ─── Classifier ──────────────────────────────────────────────────

export function classifyAppraisal(
  eprScore?:  number | null,
  score360?:  number | null,
): AppraisalClassification {
  const eprBand = bandOf(eprScore ?? undefined);
  const s360Band = bandOf(score360 ?? undefined);

  if (!eprBand || !s360Band) {
    return {
      bucket:  'incomplete',
      label:   'Awaiting data',
      meaning: 'Either the EPR total or the 360 average is missing.',
      action:  'Wait for both scores before classifying.',
      tone:    'neutral',
    };
  }

  // Canonical 5 buckets from framework
  if (eprBand === 'high'  && s360Band === 'high')  return {
    bucket:  'high_high',
    label:   'Key Talent',
    meaning: 'High performer + good behaviour. High potential, key talent retention.',
    action:  'Recognise, reward, promote. Give more responsibility.',
    tone:    'success',
  };
  if (eprBand === 'high'  && s360Band === 'low')   return {
    bucket:  'high_low',
    label:   'Behaviour Concern',
    meaning: 'Gets results but people have concerns about behaviour.',
    action:  'Coach on behaviour. Do not promote until 360 improves.',
    tone:    'warning',
  };
  if (eprBand === 'mixed' && s360Band === 'mixed') return {
    bucket:  'mixed_mixed',
    label:   'Solid Performer',
    meaning: 'Solid performer. No major issues. Room to grow.',
    action:  'Normal development plan. Standard increment.',
    tone:    'info',
  };
  if (eprBand === 'low'   && s360Band === 'high')  return {
    bucket:   'low_high',
    label:    'Possible Manager Bias',
    meaning:  'Nice person but not delivering results per manager. 360 suggests otherwise.',
    action:   'Check for manager bias. Investigate root cause. May be a role/potential mismatch.',
    tone:     'warning',
    flagBias: true,
  };
  if (eprBand === 'low'   && s360Band === 'low')   return {
    bucket:  'low_low',
    label:   'At Risk',
    meaning: 'Not delivering results + behaviour concerns.',
    action:  'Performance Improvement Plan (PIP), no increase. Exit if no improvement.',
    tone:    'error',
  };

  // Non-canonical combinations (e.g. High/Mixed, Mixed/High, Low/Mixed,
  // Mixed/Low) — the framework doesn't prescribe a fixed action; surface
  // both bands and recommend calibration judgment.
  return {
    bucket:  'borderline',
    label:   `${capitalize(eprBand)} EPR · ${capitalize(s360Band)} 360`,
    meaning: 'This combination is between the five canonical buckets.',
    action:  'Calibration discussion + manager judgment. Lean towards the dominant signal.',
    tone:    'neutral',
  };
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
